import { type ReactNode, useMemo } from "react";

import { useTranslation } from "../i18n";
import type { LayerItem } from "../types";
import { isHiddenLayer, setBranchVisibility } from "../utils/layers";

import { LayerNode } from "./LayerNode";

import "./LayerPanel.css";

export function LayerPanel({
  footer,
  hiddenLayerIds,
  root,
}: {
  footer?: ReactNode;
  hiddenLayerIds: readonly number[];
  root: LayerItem | null;
}) {
  const { t } = useTranslation();
  const treeOptions = useMemo(() => ({ hiddenLayerIds }), [hiddenLayerIds]);
  const rootChildren = useMemo(
    () =>
      root?.tree
        .getChildren()
        .filter((child) => !isHiddenLayer(child.item, treeOptions))
        .toReversed() ?? [],
    [root, treeOptions],
  );

  return (
    <aside className="layer-panel" aria-label={t("layers.panelLabel")}>
      <div className="panel-header">
        <div>
          <h1>{t("layers.title")}</h1>
        </div>
        <div className="panel-actions">
          <button
            type="button"
            title={t("layers.showAllTitle")}
            onClick={() => root && setBranchVisibility(root, true, treeOptions)}
          >
            {t("layers.showAll")}
          </button>
          <button
            type="button"
            title={t("layers.hideAllTitle")}
            onClick={() =>
              root && setBranchVisibility(root, false, treeOptions)
            }
          >
            {t("layers.hideAll")}
          </button>
        </div>
      </div>
      <div className="layer-tree" aria-live="polite">
        {root ? (
          rootChildren.map((child) => (
            <LayerNode
              key={child.id}
              layer={child}
              parentExclusive={false}
              treeOptions={treeOptions}
            />
          ))
        ) : (
          <div className="loading-row">
            <span className="spinner" />
            {t("layers.loading")}
          </div>
        )}
      </div>
      {footer}
    </aside>
  );
}
